// The breadcrumb above a device's and a site's title, and it is the scope
// (#2344). Each level — the organization, the customer, the site — is one click
// from being the scope, through the scope picker's own pick (`ScopePickLink`),
// so there is still one scope and one place it is written. The level that is
// the scope is marked. Each level's menu lists its siblings first (the other
// sites of the customer, the other customers of the organization), then its
// copy and open lines.
//
// **The levels come from the estate lists the scope picker reads, and a level
// those lists do not hold is not drawn** — never named, never counted, never a
// placeholder (§6.3). A person granted at one site sees that site alone, as the
// picker shows it (#1393).
//
// On a phone the three levels do not fit beside a title: they fold into one line
// that opens *Where this is*, each level one tap from being the scope.
import { useState } from "react";
import { createPortal } from "react-dom";
import { ClosedIcon, Glyph, OpenIcon } from "../icons";
import { Dialog } from "../surfaces";
import { EntityRef, useSheet, type Sibling } from "./EntityRef";
import type { Entity } from "./entity";
import { useEstate } from "./estate";
import { ScopePickLink, useScope, type Scope } from "./scope";

interface Level { entity: Entity; scope: Scope; kind: string; siblings: { title: string; items: Sibling[] } }

const levelName = (l: Level) => ("name" in l.entity ? l.entity.name : "");

const same = (a: Scope, b: Scope) => (a.orgId ?? "") === (b.orgId ?? "") && (a.customerId ?? "") === (b.customerId ?? "") && (a.siteId ?? "") === (b.siteId ?? "");

/** The levels above an object at `siteId`, the site included, that the lists hold. */
function useLevels(siteId: string | undefined): Level[] {
  const { lists } = useEstate();
  const { scope } = useScope();
  if (!lists || !siteId) return [];
  const site = lists.sites.find((s) => s.id === siteId);
  if (!site) return [];
  const customer = lists.customers.find((c) => c.id === site.customer_id);
  const org = lists.orgs.find((o) => o.id === site.org_id);
  const out: Level[] = [];
  if (org) {
    const s: Scope = { orgId: org.id };
    out.push({
      entity: { kind: "org", id: org.id, name: org.name, slug: org.slug }, scope: s, kind: "Organization",
      siblings: { title: "Organizations", items: lists.orgs.map((o) => ({ name: o.name, scope: { orgId: o.id }, current: same(scope, { orgId: o.id }) })) },
    });
  }
  if (customer) {
    const s: Scope = { orgId: customer.org_id, customerId: customer.id };
    out.push({
      entity: { kind: "customer", id: customer.id, name: customer.name, slug: customer.slug, orgId: customer.org_id }, scope: s, kind: "Customer",
      siblings: {
        title: org ? `Customers of ${org.name}` : "Customers",
        items: lists.customers.filter((c) => c.org_id === customer.org_id).map((c) => { const cs = { orgId: c.org_id, customerId: c.id }; return { name: c.name, scope: cs, current: same(scope, cs) }; }),
      },
    });
  }
  const ss: Scope = { orgId: site.org_id, customerId: site.customer_id, siteId: site.id };
  out.push({
    entity: { kind: "site", id: site.id, name: site.name, slug: site.slug, orgId: site.org_id, customerId: site.customer_id }, scope: ss, kind: "Site",
    siblings: {
      title: customer ? `Sites of ${customer.name}` : "Sites",
      items: lists.sites.filter((x) => x.customer_id === site.customer_id).map((x) => { const xs = { orgId: x.org_id, customerId: x.customer_id, siteId: x.id }; return { name: x.name, scope: xs, current: same(scope, xs) }; }),
    },
  });
  return out;
}

/** The breadcrumb for an object at `siteId`. Nothing while the lists load, and
 * nothing for a site they do not hold. */
export function ScopeTrail({ siteId }: { siteId?: string }) {
  const levels = useLevels(siteId);
  const { scope } = useScope();
  const sheet = useSheet();
  const [open, setOpen] = useState(false);
  if (levels.length === 0) return null;
  if (sheet) {
    return (
      <>
        <button type="button" className="btn btn-quiet scope-trail-path" aria-haspopup="dialog" onClick={() => setOpen(true)}>
          <span>{(levels.length > 1 ? levels.filter((l) => l.entity.kind !== "org") : levels).map(levelName).join(" › ")}</span>
          <Glyph icon={OpenIcon} size={12} />
        </button>
        {open && createPortal(
          <Dialog label="Where this is" className="entity-sheet" onClose={() => setOpen(false)} backdropCloses>
            <div className="entity-sheet-grab" aria-hidden="true" />
            <div className="entity-menu-head"><b>Where this is</b></div>
            <div role="menu" aria-label="Where this is" className="entity-menu-body">
              <div className="entity-menu-group">Tap a level to make it the scope</div>
              {levels.map((l) => (
                <ScopePickLink key={l.kind} scope={l.scope} role="menuitem" className="entity-menu-item" aria-current={same(scope, l.scope) || undefined} onClick={() => setOpen(false)}>
                  <span>{levelName(l)}</span>
                  <span className="entity-menu-hint">{same(scope, l.scope) ? "scope · " : ""}{l.kind}</span>
                </ScopePickLink>
              ))}
            </div>
            <button type="button" className="btn entity-sheet-cancel" onClick={() => setOpen(false)}>Cancel</button>
          </Dialog>,
          document.body,
        )}
      </>
    );
  }
  return (
    <nav className="scope-trail" aria-label="Where this is">
      {levels.map((l, i) => {
        const here = same(scope, l.scope);
        const name = levelName(l);
        return (
          <span key={l.kind} className="scope-trail-step">
            {i > 0 && <span className="scope-trail-sep" aria-hidden="true"><Glyph icon={ClosedIcon} size={12} /></span>}
            <span className={"scope-trail-level" + (here ? " is-scope" : "")}>
              <span className="scope-trail-kind">{l.kind}</span>
              <EntityRef entity={l.entity} always siblings={l.siblings}>
                <ScopePickLink scope={l.scope} aria-current={here ? "location" : undefined} title={here ? "This is the scope" : `Make ${name} the scope`}>{name}</ScopePickLink>
              </EntityRef>
            </span>
          </span>
        );
      })}
    </nav>
  );
}
